import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  List,
  // ListItem removed as it's not used
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  IconButton,
  TextField,
  InputAdornment,
  // Chip removed as it's not used
  CircularProgress,
  Button,
  Alert
} from '@mui/material';
import {
  Search,
  FileText,
  FileSpreadsheet,
  Presentation,
  File,
  Upload,
  RefreshCw,
  Plus,
  X,
  SortAsc,
  Clock
} from 'lucide-react';
import agtEduService from '../services/agtEduService';

// Componente para icono de archivo según su tipo
const FileIcon = ({ fileName, size = 20 }) => {
  const fileExt = fileName ? fileName.split('.').pop().toLowerCase() : '';
  
  if (['doc', 'docx'].includes(fileExt)) {
    return <FileText size={size} color="#4285F4" />;
  } else if (['xls', 'xlsx', 'csv'].includes(fileExt)) {
    return <FileSpreadsheet size={size} color="#0F9D58" />;
  } else if (['ppt', 'pptx'].includes(fileExt)) {
    return <Presentation size={size} color="#DB4437" />;
  } else if (fileExt === 'pdf') {
    return <File size={size} color="#FF5722" />;
  } else {
    return <File size={size} color="#607D8B" />;
  }
};

const DocumentSelector = ({ onSelectDocument, selectedDocument, colors }) => {
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date');

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Usar el servicio para obtener documentos editables
      const response = await agtEduService.getEditableDocuments();
      
      if (response.success && response.data) {
        setDocuments(response.data);
      } else {
        setError('No se pudieron cargar los documentos');
      }
    } catch (err) {
      console.error('Error al cargar documentos:', err);
      setError('Error al cargar los documentos: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Wrap filterAndSortDocuments in useCallback
  const filterAndSortDocuments = useCallback(() => {
    // Filtrar por término de búsqueda
    let filtered = documents;
    if (searchTerm) {
      filtered = documents.filter(doc => 
        doc.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Ordenar según criterio seleccionado
    let sorted = [...filtered];
    if (sortBy === 'name') {
      sorted.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortBy === 'date') {
      sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    } else if (sortBy === 'type') {
      sorted.sort((a, b) => {
        const extA = a.name.split('.').pop().toLowerCase();
        const extB = b.name.split('.').pop().toLowerCase();
        return extA.localeCompare(extB);
      });
    }
    
    setFilteredDocuments(sorted);
  }, [documents, searchTerm, sortBy]);

  // Cargar documentos al montar el componente
  useEffect(() => {
    fetchDocuments();
  }, []);

  // Filtrar y ordenar documentos cuando cambian
  useEffect(() => {
    filterAndSortDocuments();
  }, [filterAndSortDocuments]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const toggleSortBy = () => {
    // Rotar entre los criterios de ordenación
    const sortOptions = ['date', 'name', 'type'];
    const currentIndex = sortOptions.indexOf(sortBy);
    const nextIndex = (currentIndex + 1) % sortOptions.length;
    setSortBy(sortOptions[nextIndex]);
  };

  const handleSelectDocument = (document) => {
    if (onSelectDocument) {
      onSelectDocument(document);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const getSortLabel = () => {
    switch (sortBy) {
      case 'name':
        return 'Nombre';
      case 'date':
        return 'Fecha';
      case 'type':
        return 'Tipo';
      default:
        return 'Ordenar';
    }
  };

  // Cargar documentos simulados si no hay ninguno
  const simulatedDocuments = [
    {
      id: 'doc1',
      name: 'Informe Trimestral.docx',
      createdAt: new Date(Date.now() - 3600000 * 24 * 2).toISOString(),
      size: 2457600
    },
    {
      id: 'doc2',
      name: 'Presentación Proyecto.pptx',
      createdAt: new Date(Date.now() - 3600000 * 24 * 5).toISOString(),
      size: 5242880
    },
    {
      id: 'doc3',
      name: 'Datos Financieros.xlsx',
      createdAt: new Date(Date.now() - 3600000 * 24 * 10).toISOString(),
      size: 1048576
    },
    {
      id: 'doc4',
      name: 'Manual de Usuario.pdf',
      createdAt: new Date(Date.now() - 3600000 * 24 * 15).toISOString(),
      size: 3145728
    }
  ];

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Barra de búsqueda y herramientas */}
      <Box sx={{ p: 2, borderBottom: `1px solid ${colors.borderColor}` }}>
        <TextField
          fullWidth
          placeholder="Buscar documentos..."
          variant="outlined"
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={20} color={colors.textSecondary} />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClearSearch}>
                  <X size={16} />
                </IconButton>
              </InputAdornment>
            ) : null,
            sx: {
              backgroundColor: colors.inputBackground,
              borderRadius: 2,
              '& fieldset': {
                borderColor: 'transparent'
              },
              '&:hover fieldset': {
                borderColor: colors.borderColor
              }
            }
          }}
          sx={{ mb: 2 }}
        />
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            variant="outlined"
            startIcon={sortBy === 'date' ? <Clock size={16} /> : <SortAsc size={16} />}
            onClick={toggleSortBy}
            size="small"
            sx={{
              borderColor: colors.borderColor,
              color: colors.textDefault,
              textTransform: 'none'
            }}
          >
            {getSortLabel()}
          </Button>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<Upload size={16} />}
              size="small"
              sx={{
                borderColor: colors.borderColor,
                color: colors.textDefault,
                textTransform: 'none'
              }}
            >
              Subir
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshCw size={16} />}
              onClick={fetchDocuments}
              size="small"
              sx={{
                borderColor: colors.borderColor,
                color: colors.textDefault,
                textTransform: 'none'
              }}
            >
              Actualizar
            </Button>
          </Box>
        </Box>
      </Box>
      
      {/* Lista de documentos */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={30} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        ) : filteredDocuments.length > 0 ? (
          <List sx={{ width: '100%', p: 0 }}>
            {filteredDocuments.map((doc) => (
              <ListItemButton
                key={doc.id}
                selected={selectedDocument?.id === doc.id}
                onClick={() => handleSelectDocument(doc)}
                sx={{
                  borderLeft: selectedDocument?.id === doc.id 
                    ? `4px solid ${colors.accent}` 
                    : '4px solid transparent',
                  backgroundColor: selectedDocument?.id === doc.id 
                    ? `${colors.accent}10` 
                    : 'transparent',
                  '&:hover': {
                    backgroundColor: colors.backgroundSecondary
                  }
                }}
              >
                <ListItemIcon>
                  <FileIcon fileName={doc.name} />
                </ListItemIcon>
                <ListItemText
                  primary={doc.name}
                  secondary={
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                      <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                        {formatDate(doc.createdAt)}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          ml: 1, 
                          color: colors.textSecondary,
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 10 }} />
                        {(doc.size / 1024).toFixed(0)} KB
                      </Typography>
                    </Box>
                  }
                  primaryTypographyProps={{
                    color: colors.textDefault,
                    noWrap: true
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        ) : (
          // Si no hay documentos reales, mostrar documentos simulados
          filteredDocuments.length === 0 && documents.length === 0 ? (
            <List sx={{ width: '100%', p: 0 }}>
              {simulatedDocuments.map((doc) => (
                <ListItemButton
                  key={doc.id}
                  selected={selectedDocument?.id === doc.id}
                  onClick={() => handleSelectDocument(doc)}
                  sx={{
                    borderLeft: selectedDocument?.id === doc.id 
                      ? `4px solid ${colors.accent}` 
                      : '4px solid transparent',
                    backgroundColor: selectedDocument?.id === doc.id 
                      ? `${colors.accent}10` 
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: colors.backgroundSecondary
                    }
                  }}
                >
                  <ListItemIcon>
                    <FileIcon fileName={doc.name} />
                  </ListItemIcon>
                  <ListItemText
                    primary={doc.name}
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                        <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                          {formatDate(doc.createdAt)}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            ml: 1, 
                            color: colors.textSecondary,
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 10 }} />
                          {(doc.size / 1024).toFixed(0)} KB
                        </Typography>
                      </Box>
                    }
                    primaryTypographyProps={{
                      color: colors.textDefault,
                      noWrap: true
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
          ) : (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                No se encontraron documentos con "{searchTerm}"
              </Typography>
              <Button
                variant="text"
                onClick={handleClearSearch}
                sx={{ mt: 1, color: colors.accent }}
              >
                Mostrar todos
              </Button>
            </Box>
          )
        )}
      </Box>
      
      {/* Pie con botón para crear nuevo documento */}
      <Box sx={{ p: 2, borderTop: `1px solid ${colors.borderColor}` }}>
        <Button
          fullWidth
          variant="contained"
          startIcon={<Plus size={16} />}
          sx={{
            backgroundColor: colors.accent,
            color: '#FFFFFF',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: colors.accent
            }
          }}
        >
          Nuevo Documento
        </Button>
      </Box>
    </Box>
  );
};

export default DocumentSelector;